/**
 * Loop Engine (spec section 17).
 *
 * Given an audio duration and a loop length, produce the exact segment
 * plan the renderer will follow. Pure functions only - this is the piece
 * most likely to hide an off-by-one, so it is fully unit tested.
 *
 * Modes:
 *   normal    - straight back-to-back repeats (step = loop length)
 *   crossfade - consecutive loops overlap by `crossfadeSeconds`
 *               (step = loop length - crossfade)
 *   pingpong  - forward pass then reversed pass (step = 2 x loop length)
 *
 * Ping-Pong needs a *reversible* source. Procedural scenes are pure
 * functions of time, so reversing is exact. A video element cannot play
 * backwards reliably (HTMLMediaElement.playbackRate must be positive),
 * so video sources fall back to crossfade and the plan says so.
 */

import { LIMITS } from '../core/config.js';

export const LOOP_MODES = ['normal', 'crossfade', 'pingpong'];

function clampNumber(value, min, max, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

/**
 * @param {object} options
 * @param {number} options.audioDuration        length of the audio in seconds
 * @param {number} options.loopDuration         length of one loop in seconds
 * @param {'normal'|'crossfade'|'pingpong'} [options.mode]
 * @param {number} [options.crossfadeSeconds]
 * @param {boolean} [options.pingPongSupported] whether the source can be reversed
 * @returns {object} segment plan
 */
export function planLoop({
  audioDuration,
  loopDuration,
  mode = 'normal',
  crossfadeSeconds = 0.6,
  pingPongSupported = true,
  maxRepeats = LIMITS.maxLoopRepeats,
} = {}) {
  const total = clampNumber(audioDuration, 0, 24 * 3600, 0);
  const loop = clampNumber(loopDuration, 0.2, 3600, 10);

  let effectiveMode = LOOP_MODES.includes(mode) ? mode : 'normal';
  let warning = null;
  let requestedMode = effectiveMode;

  if (effectiveMode === 'pingpong' && !pingPongSupported) {
    effectiveMode = 'crossfade';
    warning =
      'Ping-Pong needs a reversible source. Imported video cannot be played backwards reliably in the browser, so this render falls back to crossfade.';
  }

  let xfade = 0;
  if (effectiveMode === 'crossfade') {
    xfade = clampNumber(crossfadeSeconds, 0, loop * 0.9, 0.6);
    if (xfade === 0) {
      effectiveMode = 'normal';
      warning = 'Crossfade length resolved to zero, so the plan uses normal repeats.';
    }
  }

  const step = effectiveMode === 'crossfade' ? loop - xfade : effectiveMode === 'pingpong' ? loop * 2 : loop;
  const segmentLength = effectiveMode === 'pingpong' ? loop * 2 : loop;

  if (total === 0) {
    return {
      mode: effectiveMode,
      requestedMode,
      audioDuration: 0,
      loopDuration: loop,
      crossfadeSeconds: xfade,
      step,
      segmentLength,
      segments: [],
      repeats: 0,
      totalDuration: 0,
      tailTruncated: false,
      warning: warning ?? 'Audio duration is zero.',
    };
  }

  const count = Math.max(1, Math.ceil(total / step));
  if (count > maxRepeats) {
    throw new Error(
      `Loop plan would need ${count} repeats (limit ${maxRepeats}). Use a longer loop or a shorter episode.`,
    );
  }

  const segments = [];
  for (let i = 0; i < count; i += 1) {
    const timelineStart = i * step;
    const timelineEnd = Math.min(total, timelineStart + segmentLength);
    const isTail = timelineEnd >= total - 1e-6;

    if (effectiveMode === 'pingpong') {
      const half = loop;
      const elapsed = timelineEnd - timelineStart;
      // The cycle is forward [0, loop] then reversed [loop, 0].
      const forwardPart = Math.min(elapsed, half);
      const reversePart = Math.max(0, elapsed - half);
      segments.push({
        index: i,
        timelineStart: round(timelineStart),
        // The forward pass ends where the reverse pass begins. Using the
        // cycle end here would make the two halves overlap.
        timelineEnd: round(timelineStart + forwardPart),
        duration: round(forwardPart),
        phase: 'forward',
        sourceStart: 0,
        sourceEnd: round(forwardPart),
        reversed: false,
        crossfadeIn: 0,
        crossfadeOut: 0,
        isTail,
      });
      if (reversePart > 0) {
        segments.push({
          index: i,
          timelineStart: round(timelineStart + half),
          timelineEnd: round(timelineEnd),
          duration: round(reversePart),
          phase: 'reverse',
          sourceStart: round(Math.max(0, loop - reversePart)),
          sourceEnd: loop,
          reversed: true,
          crossfadeIn: 0,
          crossfadeOut: 0,
          isTail,
        });
      }
      continue;
    }

    const elapsed = timelineEnd - timelineStart;
    segments.push({
      index: i,
      timelineStart: round(timelineStart),
      timelineEnd: round(timelineEnd),
      duration: round(elapsed),
      phase: 'forward',
      sourceStart: 0,
      sourceEnd: round(Math.min(loop, elapsed)),
      reversed: false,
      crossfadeIn: i === 0 ? 0 : xfade,
      crossfadeOut: isTail ? 0 : xfade,
      isTail,
    });
  }

  return {
    mode: effectiveMode,
    requestedMode,
    audioDuration: round(total),
    loopDuration: round(loop),
    crossfadeSeconds: round(xfade),
    step: round(step),
    segmentLength: round(segmentLength),
    segments,
    repeats: count,
    totalDuration: round(total),
    tailTruncated: segments.length > 0 && segments[segments.length - 1].duration < loop - 1e-6,
    warning,
  };
}

/**
 * Map a timeline position to the source position for a plan.
 * Used by the renderer and heavily exercised by the tests.
 */
export function resolveSegmentAt(plan, timelineSeconds) {
  if (!plan?.segments?.length) return null;
  const t = Math.max(0, timelineSeconds);
  for (const segment of plan.segments) {
    if (t >= segment.timelineStart - 1e-9 && t < segment.timelineEnd + 1e-9) {
      const local = t - segment.timelineStart;
      const sourceTime = segment.reversed
        ? Math.max(0, segment.sourceEnd - local)
        : Math.min(segment.sourceEnd, segment.sourceStart + local);
      return { segment, local, sourceTime };
    }
  }
  const last = plan.segments[plan.segments.length - 1];
  return { segment: last, local: last.duration, sourceTime: last.sourceEnd };
}

/** Crossfade weight (0..1) for the second of two overlapping loops. */
export function crossfadeWeight(secondsIntoOverlap, crossfadeSeconds) {
  if (!crossfadeSeconds || crossfadeSeconds <= 0) return 1;
  const k = Math.min(1, Math.max(0, secondsIntoOverlap / crossfadeSeconds));
  // smoothstep - linear crossfades are visible on high-contrast footage
  return k * k * (3 - 2 * k);
}

/** Human summary for the render panel. */
export function describePlan(plan) {
  if (!plan || !plan.segments.length) return 'No plan.';
  const modeLabel = { normal: 'Normal', crossfade: 'Crossfade', pingpong: 'Ping-Pong' }[plan.mode] ?? plan.mode;
  const parts = [
    `${plan.repeats} x ${plan.loopDuration}s loop`,
    modeLabel,
    `covers ${plan.audioDuration}s of audio`,
  ];
  if (plan.mode === 'crossfade') parts.push(`${plan.crossfadeSeconds}s overlap`);
  if (plan.warning) parts.push('(fallback applied)');
  return parts.join(' \u00b7 ');
}

function round(value) {
  return Math.round(value * 10000) / 10000;
}
